/**
 * IECN delivery lifecycle — a controlled state machine.
 *
 * A delivery's status may only change through `canTransition` / `assertTransition`.
 * Nothing in the API is permitted to write `delivery.status` directly.
 * See docs/DELIVERY_STATE_MACHINE.md.
 */
export const DELIVERY_STATUSES = [
    'DRAFT',
    'QUOTED',
    'PENDING_PAYMENT',
    'CONFIRMED',
    'UNASSIGNED',
    'OFFERED',
    'ASSIGNED',
    'EN_ROUTE_PICKUP',
    'ARRIVED_PICKUP',
    'WAITING_PICKUP',
    'PICKED_UP',
    'IN_TRANSIT',
    'ARRIVED_DROPOFF',
    'WAITING_DROPOFF',
    'POD_PENDING',
    'DELIVERED',
    'CLOSED',
    'ON_HOLD',
    'EXCEPTION',
    'FAILED',
    'RETURN_REQUIRED',
    'RETURN_IN_TRANSIT',
    'RETURNED',
    'CANCELLED',
];
/**
 * Allowed transitions. Anything not listed here is rejected.
 * ON_HOLD / EXCEPTION are reachable from any live state (see `ALWAYS_REACHABLE`).
 */
const TRANSITIONS = {
    DRAFT: ['QUOTED', 'CANCELLED'],
    QUOTED: ['PENDING_PAYMENT', 'CONFIRMED', 'CANCELLED'],
    PENDING_PAYMENT: ['CONFIRMED', 'CANCELLED'],
    // CONFIRMED means money is secured (authorised, captured, or billed to account terms).
    CONFIRMED: ['UNASSIGNED', 'CANCELLED'],
    UNASSIGNED: ['OFFERED', 'ASSIGNED', 'CANCELLED'],
    OFFERED: ['ASSIGNED', 'UNASSIGNED', 'CANCELLED'],
    ASSIGNED: ['EN_ROUTE_PICKUP', 'UNASSIGNED', 'CANCELLED'],
    EN_ROUTE_PICKUP: ['ARRIVED_PICKUP', 'UNASSIGNED', 'CANCELLED'],
    ARRIVED_PICKUP: ['WAITING_PICKUP', 'PICKED_UP', 'FAILED'],
    WAITING_PICKUP: ['PICKED_UP', 'FAILED'],
    // Custody has begun at PICKED_UP. Plain cancellation is no longer reachable.
    PICKED_UP: ['IN_TRANSIT', 'ARRIVED_DROPOFF'],
    IN_TRANSIT: ['ARRIVED_DROPOFF'],
    ARRIVED_DROPOFF: ['WAITING_DROPOFF', 'POD_PENDING', 'DELIVERED', 'FAILED'],
    WAITING_DROPOFF: ['POD_PENDING', 'DELIVERED', 'FAILED'],
    POD_PENDING: ['DELIVERED', 'FAILED'],
    DELIVERED: ['CLOSED'],
    CLOSED: [],
    ON_HOLD: [
        'UNASSIGNED',
        'ASSIGNED',
        'EN_ROUTE_PICKUP',
        'IN_TRANSIT',
        'CANCELLED',
        'RETURN_REQUIRED',
    ],
    EXCEPTION: [
        'UNASSIGNED',
        'ASSIGNED',
        'EN_ROUTE_PICKUP',
        'ARRIVED_PICKUP',
        'IN_TRANSIT',
        'ARRIVED_DROPOFF',
        'POD_PENDING',
        'DELIVERED',
        'FAILED',
        'RETURN_REQUIRED',
        'CANCELLED',
    ],
    FAILED: ['RETURN_REQUIRED', 'UNASSIGNED', 'CLOSED', 'ON_HOLD'],
    RETURN_REQUIRED: ['RETURN_IN_TRANSIT', 'CLOSED'],
    RETURN_IN_TRANSIT: ['RETURNED'],
    RETURNED: ['CLOSED'],
    CANCELLED: [],
};
/** Terminal states — nothing may leave them. */
export const TERMINAL_STATUSES = ['CLOSED', 'CANCELLED'];
/** States an operator can push a live delivery into from anywhere that is not terminal. */
const ALWAYS_REACHABLE = ['ON_HOLD', 'EXCEPTION'];
/** Custody has begun — cancellation must go through the return/exception workflow instead. */
export const CUSTODY_STATUSES = [
    'PICKED_UP',
    'IN_TRANSIT',
    'ARRIVED_DROPOFF',
    'WAITING_DROPOFF',
    'POD_PENDING',
    'RETURN_REQUIRED',
    'RETURN_IN_TRANSIT',
];
/** States that count as live work on the dispatch board. */
export const ACTIVE_STATUSES = [
    'UNASSIGNED',
    'OFFERED',
    'ASSIGNED',
    'EN_ROUTE_PICKUP',
    'ARRIVED_PICKUP',
    'WAITING_PICKUP',
    'PICKED_UP',
    'IN_TRANSIT',
    'ARRIVED_DROPOFF',
    'WAITING_DROPOFF',
    'POD_PENDING',
];
/** States where a driver is assigned and expected to be moving. */
export const DRIVER_ENGAGED_STATUSES = [
    'ASSIGNED',
    'EN_ROUTE_PICKUP',
    'ARRIVED_PICKUP',
    'WAITING_PICKUP',
    'PICKED_UP',
    'IN_TRANSIT',
    'ARRIVED_DROPOFF',
    'WAITING_DROPOFF',
    'POD_PENDING',
];
export function isTerminal(status) {
    return TERMINAL_STATUSES.includes(status);
}
export function hasCustody(status) {
    return CUSTODY_STATUSES.includes(status);
}
export function isActive(status) {
    return ACTIVE_STATUSES.includes(status);
}
export function canTransition(from, to) {
    if (from === to)
        return false;
    if (isTerminal(from))
        return false;
    if (ALWAYS_REACHABLE.includes(to))
        return true;
    return (TRANSITIONS[from] ?? []).includes(to);
}
export function allowedTransitions(from) {
    if (isTerminal(from))
        return [];
    return [...new Set([...(TRANSITIONS[from] ?? []), ...ALWAYS_REACHABLE])];
}
export class IllegalTransitionError extends Error {
    from;
    to;
    code = 'ILLEGAL_STATE_TRANSITION';
    constructor(from, to) {
        super(`Delivery cannot move from ${from} to ${to}. Allowed from ${from}: ${allowedTransitions(from).join(', ') || '(terminal state)'}`);
        this.from = from;
        this.to = to;
        this.name = 'IllegalTransitionError';
    }
}
export function assertTransition(from, to) {
    if (!canTransition(from, to))
        throw new IllegalTransitionError(from, to);
}
/** Customer-facing label. Internal operational states are deliberately softened. */
export function customerStatusLabel(status) {
    const labels = {
        DRAFT: 'Draft',
        QUOTED: 'Quote ready',
        PENDING_PAYMENT: 'Awaiting payment',
        CONFIRMED: 'Booked',
        UNASSIGNED: 'Booked',
        OFFERED: 'Assigning courier',
        ASSIGNED: 'Courier assigned',
        EN_ROUTE_PICKUP: 'Courier en route to pickup',
        ARRIVED_PICKUP: 'Courier at pickup',
        WAITING_PICKUP: 'Waiting at pickup',
        PICKED_UP: 'Picked up',
        IN_TRANSIT: 'In transit',
        ARRIVED_DROPOFF: 'Arriving now',
        WAITING_DROPOFF: 'Waiting at destination',
        POD_PENDING: 'Completing delivery',
        DELIVERED: 'Delivered',
        CLOSED: 'Delivered',
        ON_HOLD: 'On hold',
        EXCEPTION: 'Needs attention',
        FAILED: 'Delivery attempt failed',
        RETURN_REQUIRED: 'Return required',
        RETURN_IN_TRANSIT: 'Returning to sender',
        RETURNED: 'Returned to sender',
        CANCELLED: 'Cancelled',
    };
    return labels[status];
}
/** Progress 0..1 for the customer tracking timeline. */
export function customerProgress(status) {
    const steps = {
        CONFIRMED: 0.1,
        UNASSIGNED: 0.1,
        OFFERED: 0.15,
        ASSIGNED: 0.25,
        EN_ROUTE_PICKUP: 0.35,
        ARRIVED_PICKUP: 0.45,
        WAITING_PICKUP: 0.45,
        PICKED_UP: 0.6,
        IN_TRANSIT: 0.75,
        ARRIVED_DROPOFF: 0.9,
        WAITING_DROPOFF: 0.9,
        POD_PENDING: 0.95,
        DELIVERED: 1,
        CLOSED: 1,
        RETURNED: 1,
    };
    return steps[status] ?? 0;
}
export function statusTone(status) {
    switch (status) {
        case 'DRAFT':
        case 'QUOTED':
            return 'neutral';
        case 'PENDING_PAYMENT':
        case 'CONFIRMED':
        case 'UNASSIGNED':
        case 'OFFERED':
            return 'info';
        case 'ASSIGNED':
        case 'EN_ROUTE_PICKUP':
        case 'ARRIVED_PICKUP':
        case 'PICKED_UP':
        case 'IN_TRANSIT':
        case 'ARRIVED_DROPOFF':
        case 'POD_PENDING':
            return 'active';
        case 'DELIVERED':
        case 'CLOSED':
        case 'RETURNED':
            return 'success';
        case 'WAITING_PICKUP':
        case 'WAITING_DROPOFF':
        case 'ON_HOLD':
        case 'RETURN_REQUIRED':
        case 'RETURN_IN_TRANSIT':
            return 'warning';
        case 'EXCEPTION':
        case 'FAILED':
        case 'CANCELLED':
            return 'critical';
        default:
            return 'neutral';
    }
}
/** SLA risk banding driven by the ETA vs the committed deadline. */
export const SLA_STATES = ['ON_TIME', 'WATCH', 'AT_RISK', 'LATE'];
/**
 * WATCH  — projected to land within 10 minutes of the deadline.
 * AT_RISK — projected to miss the deadline but the deadline has not passed yet.
 * LATE   — deadline has passed and the delivery is not complete.
 */
export function evaluateSla({ deadline, projected, now = new Date() }) {
    if (!deadline)
        return 'ON_TIME';
    const due = new Date(deadline).getTime();
    if (!Number.isFinite(due))
        return 'ON_TIME';
    if (now.getTime() > due)
        return 'LATE';
    if (!projected)
        return 'ON_TIME';
    const eta = new Date(projected).getTime();
    if (!Number.isFinite(eta))
        return 'ON_TIME';
    if (eta > due)
        return 'AT_RISK';
    if (due - eta <= 10 * 60 * 1000)
        return 'WATCH';
    return 'ON_TIME';
}
//# sourceMappingURL=delivery-state.js.map